import { type Ref } from 'vue'
import type { CropRegion, CroppedResult, ImageDisplayParams, SelectedRegionId } from '../types'

export function useCropRegions(
  canvas: Ref<HTMLCanvasElement | null>,
  image: Ref<HTMLImageElement | null>,
  cropRegions: Ref<CropRegion[]>,
  selectedRegionId: Ref<string | null>,
  croppedResults: Ref<CroppedResult[]>,
  originalCroppedResults: Ref<CroppedResult[]>,
  baseCroppedResults: Ref<CroppedResult[]>,
  pristineCroppedResults: Ref<CroppedResult[]>,
  showCropFeedback: Ref<boolean>,
  imageDisplayParams: Ref<ImageDisplayParams | null>,
  generateId: () => string
) {
  function initCropRegions() {
    if (!canvas.value) return

    // Create initial crop region that covers most of the visible image area
    const margin = 0.1 // 10% margin = 90% of canvas (covers most of the document)

    // Use CSS display dimensions (not buffer dimensions) for crop region positioning
    // Crop regions are stored in CSS display coordinates
    const actualWidth = parseFloat(canvas.value.style.width) || canvas.value.clientWidth || 748
    const actualHeight = parseFloat(canvas.value.style.height) || canvas.value.clientHeight || 800

    const initialRegion: CropRegion = {
      id: generateId(),
      vertices: [
        { x: actualWidth * margin, y: actualHeight * margin }, // top-left
        { x: actualWidth * (1 - margin), y: actualHeight * margin }, // top-right
        { x: actualWidth * (1 - margin), y: actualHeight * (1 - margin) }, // bottom-right
        { x: actualWidth * margin, y: actualHeight * (1 - margin) } // bottom-left
      ],
      isActive: true
    }

    cropRegions.value = [initialRegion]
    selectedRegionId.value = initialRegion.id
  }

  function addNewCropRegion(draw?: () => void) {
    if (!canvas.value) return

    // Use CSS display dimensions (not buffer dimensions) for crop region positioning
    const actualWidth = parseFloat(canvas.value.style.width) || canvas.value.clientWidth || 748
    const actualHeight = parseFloat(canvas.value.style.height) || canvas.value.clientHeight || 800

    // Create a smaller square region in the center
    const size = Math.min(actualWidth, actualHeight) * 0.3 // 30% of the smaller dimension
    const centerX = actualWidth / 2
    const centerY = actualHeight / 2
    const halfSize = size / 2

    const newRegion: CropRegion = {
      id: generateId(),
      vertices: [
        { x: centerX - halfSize, y: centerY - halfSize }, // top-left
        { x: centerX + halfSize, y: centerY - halfSize }, // top-right
        { x: centerX + halfSize, y: centerY + halfSize }, // bottom-right
        { x: centerX - halfSize, y: centerY + halfSize } // bottom-left
      ],
      isActive: true
    }

    // Set all existing regions to inactive
    cropRegions.value.forEach((region) => {
      region.isActive = false
    })

    // Add the new region and make it active
    cropRegions.value.push(newRegion)
    selectedRegionId.value = newRegion.id

    // Redraw canvas to show the new region
    if (draw) {
      draw()
    }
  }

  function removeCropRegion(regionId: string, selectedCroppedRegionId?: Ref<SelectedRegionId>) {
    if (cropRegions.value.length <= 1) {
      return
    }

    // Remove the crop region
    cropRegions.value = cropRegions.value.filter((region) => region.id !== regionId)

    // Remove corresponding cropped result if it exists
    croppedResults.value = croppedResults.value.filter((result) => result.regionId !== regionId)
    originalCroppedResults.value = originalCroppedResults.value.filter(
      (result) => result.regionId !== regionId
    )
    baseCroppedResults.value = baseCroppedResults.value.filter(
      (result) => result.regionId !== regionId
    )

    // If the removed region was active, set the first region as active
    if (selectedRegionId.value === regionId) {
      selectedRegionId.value = cropRegions.value[0]?.id || null
    }

    // If we have cropped results and selectedCroppedRegionId is provided, select "All pages"
    if (croppedResults.value.length > 0 && selectedCroppedRegionId) {
      selectedCroppedRegionId.value = 'all'
    }
  }

  function removeRegionByIndex(index: number) {
    if (cropRegions.value.length <= 1) {
      return // Don't remove the last region
    }

    const regionToRemove = cropRegions.value[index]
    removeCropRegion(regionToRemove.id)
  }

  function setActiveRegion(regionId: string) {
    selectedRegionId.value = regionId
    cropRegions.value.forEach((region) => {
      region.isActive = region.id === regionId
    })
  }

  function fitToCanvas() {
    if (!canvas.value) return

    // Create a crop region that covers the entire canvas (using CSS display dimensions)
    const displayWidth = parseFloat(canvas.value.style.width) || canvas.value.clientWidth || 1200
    const displayHeight = parseFloat(canvas.value.style.height) || canvas.value.clientHeight || 800

    const fullRegion: CropRegion = {
      id: generateId(),
      vertices: [
        { x: 0, y: 0 }, // top-left
        { x: displayWidth, y: 0 }, // top-right
        { x: displayWidth, y: displayHeight }, // bottom-right
        { x: 0, y: displayHeight } // bottom-left
      ],
      isActive: true
    }

    // Replace all regions with this full canvas region
    cropRegions.value = [fullRegion]
    selectedRegionId.value = fullRegion.id
  }

  function fitToImage() {
    if (!canvas.value) return

    // Create a crop region that covers most of the image (with small margin)
    const margin = 0.05 // 5% margin = 90% of the canvas

    // Use CSS display dimensions for crop region positioning
    const displayWidth = parseFloat(canvas.value.style.width) || canvas.value.clientWidth || 1200
    const displayHeight = parseFloat(canvas.value.style.height) || canvas.value.clientHeight || 800

    const imageRegion: CropRegion = {
      id: generateId(),
      vertices: [
        { x: displayWidth * margin, y: displayHeight * margin }, // top-left
        { x: displayWidth * (1 - margin), y: displayHeight * margin }, // top-right
        { x: displayWidth * (1 - margin), y: displayHeight * (1 - margin) }, // bottom-right
        { x: displayWidth * margin, y: displayHeight * (1 - margin) } // bottom-left
      ],
      isActive: true
    }

    // Replace all regions with this image-focused region
    cropRegions.value = [imageRegion]
    selectedRegionId.value = imageRegion.id
  }

  function applyCrop(updateCanvas?: () => void) {
    if (!canvas.value || !image.value || !imageDisplayParams.value) return

    // Clear previous results
    croppedResults.value = []

    // Process all crop regions
    cropRegions.value.forEach((region, index) => {
      processCropRegion(region, index)
    })

    // Show success feedback
    showCropFeedback.value = true
    setTimeout(() => {
      showCropFeedback.value = false
    }, 2000)

    // Store original cropped results for reset functionality
    originalCroppedResults.value = croppedResults.value.map((result) => ({
      ...result,
      imageData: result.imageData // Create a copy
    }))

    // Store base cropped results (before any trims) for trim reset functionality
    baseCroppedResults.value = croppedResults.value.map((result) => ({
      ...result,
      imageData: result.imageData // Create a copy
    }))

    // Store pristine cropped results (never modified, for rotation reset)
    pristineCroppedResults.value = croppedResults.value.map((result) => ({
      ...result,
      imageData: result.imageData // Create a copy
    }))

    // Update canvas to show cropped results after a small delay
    if (updateCanvas) {
      setTimeout(() => {
        updateCanvas()
      }, 50)
    }
  }

  function processCropRegion(region: CropRegion, index: number) {
    if (!image.value || !imageDisplayParams.value || !canvas.value) return

    // Convert from CSS display coordinates to canvas buffer coordinates first
    // The crop regions are drawn/stored in CSS display coordinates, but the canvas buffer
    // is now at native resolution, so we need to scale up the coordinates
    const cssWidth = parseFloat(canvas.value.style.width) || canvas.value.clientWidth
    const cssHeight = parseFloat(canvas.value.style.height) || canvas.value.clientHeight

    // Scale from CSS display coordinates to canvas buffer coordinates
    const cssToBufferScaleX = canvas.value.width / cssWidth
    const cssToBufferScaleY = canvas.value.height / cssHeight

    // Convert canvas display coordinates to original high-resolution image coordinates
    // Account for rotation, offset, and scaling
    const { offsetX, offsetY, scale, rotation, canvasWidth, canvasHeight } =
      imageDisplayParams.value

    const fullResVertices = region.vertices.map((vertex) => {
      // First, scale from CSS display coordinates to canvas buffer coordinates
      let x = vertex.x * cssToBufferScaleX
      let y = vertex.y * cssToBufferScaleY

      // If image is rotated, we need to transform coordinates back to the original image space
      if (rotation !== 0) {
        // Step 1: Transform from canvas coordinates to the rotated image coordinate system
        // The image is drawn at (0,0) in the rotated coordinate system after translation

        // First, translate to center of canvas
        x -= canvasWidth / 2
        y -= canvasHeight / 2

        // Apply inverse rotation to get back to original orientation
        const rotationRad = (-rotation * Math.PI) / 180 // Negative for inverse
        const cos = Math.cos(rotationRad)
        const sin = Math.sin(rotationRad)

        const rotatedX = x * cos - y * sin
        const rotatedY = x * sin + y * cos

        // Now we're in the original coordinate system
        // The image is centered, so translate to get image coordinates
        const imageWidth = image.value!.width * scale
        const imageHeight = image.value!.height * scale

        return {
          x: (rotatedX + imageWidth / 2) / scale,
          y: (rotatedY + imageHeight / 2) / scale
        }
      } else {
        // No rotation - convert from buffer canvas coordinates to original image coordinates
        // Since scale is 1.0 (native resolution), these coordinates are already at the image resolution
        // We just need to account for offset
        return {
          x: (x - offsetX) / scale,
          y: (y - offsetY) / scale
        }
      }
    })

    // Clamp coordinates to image bounds
    const clampedVertices = fullResVertices.map((vertex) => ({
      x: Math.max(0, Math.min(image.value!.width, vertex.x)),
      y: Math.max(0, Math.min(image.value!.height, vertex.y))
    }))

    // Apply PERSPECTIVE TRANSFORMATION for this region
    const croppedImageData = applyPerspectiveTransformationToRegion(clampedVertices)

    if (croppedImageData) {
      croppedResults.value.push({
        id: `crop_${index + 1}`,
        imageData: croppedImageData,
        regionId: region.id
      })
    }
  }

  function applyPerspectiveTransformationToRegion(
    vertices: Array<{ x: number; y: number }>
  ): string | null {
    if (!image.value) return null

    // Calculate the dimensions based on the selected area
    const srcPoints = vertices.map((v) => [v.x, v.y])

    // Calculate the dimensions of the document based on the selected points
    const width1 = Math.sqrt(
      Math.pow(vertices[1].x - vertices[0].x, 2) + Math.pow(vertices[1].y - vertices[0].y, 2)
    )
    const width2 = Math.sqrt(
      Math.pow(vertices[2].x - vertices[3].x, 2) + Math.pow(vertices[2].y - vertices[3].y, 2)
    )
    const height1 = Math.sqrt(
      Math.pow(vertices[3].x - vertices[0].x, 2) + Math.pow(vertices[3].y - vertices[0].y, 2)
    )
    const height2 = Math.sqrt(
      Math.pow(vertices[2].x - vertices[1].x, 2) + Math.pow(vertices[2].y - vertices[1].y, 2)
    )

    // Use the maximum dimensions to preserve detail
    const outputWidth = Math.round(Math.max(width1, width2))
    const outputHeight = Math.round(Math.max(height1, height2))

    if (outputWidth <= 0 || outputHeight <= 0) {
      console.warn('Invalid output dimensions:', { outputWidth, outputHeight })
      return null
    }

    // Create result canvas at the calculated output size
    const resultCanvas = document.createElement('canvas')
    const resultCtx = resultCanvas.getContext('2d')
    if (!resultCtx) return null

    resultCanvas.width = outputWidth
    resultCanvas.height = outputHeight

    // Apply perspective correction using bilinear interpolation with original high-res image
    bilinearTransform(image.value, srcPoints, outputWidth, outputHeight, resultCtx)

    // Return the cropped image data (adjustments will be applied later)
    const imageData = resultCanvas.toDataURL('image/png', 1.0)

    return imageData
  }

  function bilinearTransform(
    sourceImage: HTMLImageElement,
    srcPoints: number[][],
    outputWidth: number,
    outputHeight: number,
    resultCtx: CanvasRenderingContext2D
  ) {
    // Create temporary canvas for source image
    const tempCanvas = document.createElement('canvas')
    const tempCtx = tempCanvas.getContext('2d')
    if (!tempCtx) return

    tempCanvas.width = sourceImage.width
    tempCanvas.height = sourceImage.height
    tempCtx.drawImage(sourceImage, 0, 0)

    const sourceImageData = tempCtx.getImageData(0, 0, tempCanvas.width, tempCanvas.height)
    const resultImageData = resultCtx.createImageData(outputWidth, outputHeight)

    // Define destination rectangle corners
    const dstPoints = [
      [0, 0],
      [outputWidth, 0],
      [outputWidth, outputHeight],
      [0, outputHeight]
    ]

    for (let y = 0; y < outputHeight; y++) {
      for (let x = 0; x < outputWidth; x++) {
        // Map destination pixel to source using bilinear interpolation
        const srcCoord = mapPointQuadToQuad(x, y, dstPoints, srcPoints)
        const srcX = srcCoord[0]
        const srcY = srcCoord[1]

        if (srcX >= 0 && srcX < tempCanvas.width && srcY >= 0 && srcY < tempCanvas.height) {
          // Bilinear interpolation
          const x1 = Math.floor(srcX)
          const y1 = Math.floor(srcY)
          const x2 = Math.min(x1 + 1, tempCanvas.width - 1)
          const y2 = Math.min(y1 + 1, tempCanvas.height - 1)

          const dx = srcX - x1
          const dy = srcY - y1

          // Get pixel values at four corners
          const getPixel = (x: number, y: number) => {
            const idx = (y * tempCanvas.width + x) * 4
            return [
              sourceImageData.data[idx],
              sourceImageData.data[idx + 1],
              sourceImageData.data[idx + 2],
              sourceImageData.data[idx + 3]
            ]
          }

          const p1 = getPixel(x1, y1)
          const p2 = getPixel(x2, y1)
          const p3 = getPixel(x1, y2)
          const p4 = getPixel(x2, y2)

          // Interpolate
          const resultPixel = [0, 0, 0, 0]
          for (let i = 0; i < 4; i++) {
            const top = p1[i] * (1 - dx) + p2[i] * dx
            const bottom = p3[i] * (1 - dx) + p4[i] * dx
            resultPixel[i] = Math.round(top * (1 - dy) + bottom * dy)
          }

          // Set result pixel
          const dstIdx = (y * outputWidth + x) * 4
          resultImageData.data[dstIdx] = resultPixel[0]
          resultImageData.data[dstIdx + 1] = resultPixel[1]
          resultImageData.data[dstIdx + 2] = resultPixel[2]
          resultImageData.data[dstIdx + 3] = 255
        }
      }
    }

    resultCtx.putImageData(resultImageData, 0, 0)
  }

  function mapPointQuadToQuad(
    x: number,
    y: number,
    fromQuad: number[][],
    toQuad: number[][]
  ): number[] {
    // Use bilinear interpolation to map from rectangle to quadrilateral
    const u = x / (fromQuad[1][0] - fromQuad[0][0])
    const v = y / (fromQuad[2][1] - fromQuad[0][1])

    // Bilinear interpolation in the source quadrilateral
    const top = [
      toQuad[0][0] * (1 - u) + toQuad[1][0] * u,
      toQuad[0][1] * (1 - u) + toQuad[1][1] * u
    ]

    const bottom = [
      toQuad[3][0] * (1 - u) + toQuad[2][0] * u,
      toQuad[3][1] * (1 - u) + toQuad[2][1] * u
    ]

    return [top[0] * (1 - v) + bottom[0] * v, top[1] * (1 - v) + bottom[1] * v]
  }

  return {
    initCropRegions,
    addNewCropRegion,
    removeCropRegion,
    removeRegionByIndex,
    setActiveRegion,
    fitToCanvas,
    fitToImage,
    applyCrop,
    processCropRegion,
    applyPerspectiveTransformationToRegion,
    bilinearTransform,
    mapPointQuadToQuad
  }
}
